import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sparkles, Zap, Share2, TrendingUp, Download, Lock, Crown, Check, QrCode, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
  const [country, setCountry] = React.useState('IN');
  const [price, setPrice] = React.useState('₹799');
  const [uploadingProof, setUploadingProof] = React.useState(false);
  const [paymentEmail, setPaymentEmail] = React.useState('');

  React.useEffect(() => {
    // Detect user location
    fetch('https://ipapi.co/json/')
      .then(res => res.json())
      .then(data => {
        const userCountry = data.country_code;
        setCountry(userCountry);
        if (userCountry === 'IN') {
          setPrice('₹799');
        } else {
          setPrice('$10');
        }
      })
      .catch(() => {
        setPrice('₹799');
      });

    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/payment-button.js';
    script.setAttribute('data-payment_button_id', 'pl_SS7xxgRqoPOeaw');
    script.async = true;
    
    if (showUpgradeModal) {
      const container = document.getElementById('razorpay-button-container');
      if (container) {
        container.innerHTML = '';
        container.appendChild(script);
      }
    }
  }, [showUpgradeModal]);

  const handleUploadPaymentProof = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!paymentEmail) {
      toast.error('Please enter your email first');
      return;
    }

    setUploadingProof(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('email', paymentEmail);

    try {
      await axios.post(`${API}/payment/upload-proof-guest`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Payment proof uploaded! We will verify and contact you via email within 24 hours.');
      setShowUpgradeModal(false);
    } catch (error) {
      toast.error('Failed to upload payment proof');
    } finally {
      setUploadingProof(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass h-20 flex items-center justify-between px-8">
        <div className="flex items-center gap-2">
          <Sparkles className="h-8 w-8 text-primary" />
          <span className="text-2xl font-bold gradient-text">DashAI</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/login">
            <Button variant="ghost" data-testid="header-login-btn">Sign In</Button>
          </Link>
          <Link to="/register">
            <Button data-testid="header-register-btn" className="rounded-full px-6">Get Started</Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-bg min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
        <div className="container mx-auto px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6">
              Transform Data into
              <span className="block gradient-text mt-2">Intelligent Dashboards</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-12 max-w-2xl mx-auto">
              Upload your CSV or Excel file and let AI create stunning, professional dashboards in seconds.
              Built by expert data analysts, powered by cutting-edge AI.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" data-testid="hero-cta-btn" className="rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-primary/25 hover:scale-105 transition-transform">
                  <Sparkles className="mr-2 h-5 w-5" />
                  Start Free Trial
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="secondary" className="rounded-full px-8 py-6 text-lg font-medium border border-white/10">
                  Sign In
                </Button>
              </Link>
            </div>
            <p className="text-sm text-muted-foreground/80 mt-6">
              3 free dashboards • No credit card required
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-8">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">Powerful Features</h2>
            <p className="text-base md:text-lg text-muted-foreground">Everything you need to visualize your data</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: 'AI-Powered Analysis',
                description: 'Advanced GPT-5.2 technology analyzes your data like a senior data analyst with 20+ years experience',
                color: 'text-primary'
              },
              {
                icon: TrendingUp,
                title: 'Smart Visualizations',
                description: 'Automatically generates the most relevant charts and graphs based on your data patterns',
                color: 'text-accent'
              },
              {
                icon: Download,
                title: 'Excel Export',
                description: 'Download your dashboard insights and analysis as Excel files for presentations',
                color: 'text-chart-3'
              },
              {
                icon: Share2,
                title: 'Easy Sharing',
                description: 'Share dashboards with colleagues via secure links. No login required for viewers',
                color: 'text-chart-4'
              },
              {
                icon: Lock,
                title: 'Secure & Private',
                description: 'Your data is encrypted and private. Only you control who can see your dashboards',
                color: 'text-chart-5'
              },
              {
                icon: Sparkles,
                title: 'Instant Results',
                description: 'Get professional dashboards in seconds, not hours. Upload, analyze, share - done',
                color: 'text-primary'
              }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-zinc-950/50 backdrop-blur-md border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all card-hover"
                  data-testid={`feature-card-${index}`}
                >
                  <Icon className={`h-12 w-12 ${feature.color} mb-4`} />
                  <h3 className="text-2xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-8 bg-zinc-950/30">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">Loved by Data Professionals</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Chen',
                role: 'Data Scientist',
                avatar: 'https://images.unsplash.com/photo-1758518727592-706e80ebc354?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwbW9kZXJufGVufDB8fHx8MTc3MzY4NjU3NHww&ixlib=rb-4.1.0&q=85',
                quote: 'DashAI saves me hours every week. The AI insights are incredibly accurate and actionable.'
              },
              {
                name: 'Marcus Thorne',
                role: 'CTO',
                avatar: 'https://images.unsplash.com/photo-1758599543147-86dc1512bd5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwbW9kZXJufGVufDB8fHx8MTc3MzY4NjU3NHww&ixlib=rb-4.1.0&q=85',
                quote: 'Best investment for our team. Makes data-driven decisions faster and more confident.'
              },
              {
                name: 'Elena Rodriguez',
                role: 'Marketing Director',
                avatar: 'https://images.unsplash.com/photo-1770363756771-c288cfd6a3dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwbW9kZXJufGVufDB8fHx8MTc3MzY4NjU3NHww&ixlib=rb-4.1.0&q=85',
                quote: 'The sharing feature is perfect. I can send insights to stakeholders instantly.'
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-zinc-900 to-black border border-primary/20 rounded-xl p-6 relative overflow-hidden"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <img src={testimonial.avatar} alt={testimonial.name} className="w-16 h-16 rounded-full object-cover" />
                  <div>
                    <div className="font-semibold text-lg">{testimonial.name}</div>
                    <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed">"{testimonial.quote}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="container mx-auto max-width-4xl text-center glass rounded-2xl p-12"
        >
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">Ready to Get Started?</h2>
          <p className="text-base md:text-lg text-muted-foreground mb-8">
            Join thousands of professionals using DashAI to make better data-driven decisions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" data-testid="cta-bottom-btn" className="rounded-full px-10 py-6 text-lg font-semibold shadow-lg shadow-primary/25 hover:scale-105 transition-transform">
                Start Free Trial Now
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => setShowUpgradeModal(true)}
              className="rounded-full px-10 py-6 text-lg font-semibold border-2 border-primary hover:scale-105 transition-transform"
              data-testid="upgrade-home-btn"
            >
              <Crown className="h-5 w-5 mr-2" />
              Upgrade to Premium
            </Button>
          </div>
        </motion.div>
      </section>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowUpgradeModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-3xl font-bold mb-2">Upgrade to Premium</h2>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-primary">{price}</span>
                  <span className="text-muted-foreground">one-time payment</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {country === 'IN' ? '🇮🇳 India Price' : '🌍 International Price'}
                </p>
              </div>
              <button 
                onClick={() => setShowUpgradeModal(false)}
                className="text-muted-foreground hover:text-foreground text-2xl"
              >
                ×
              </button>
            </div>

            <div className="mb-6 bg-primary/10 border border-primary/20 rounded-xl p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Crown className="h-5 w-5 text-primary" />
                Premium Features:
              </h3>
              <ul className="space-y-2">
                {[
                  'Unlimited AI dashboards',
                  'All export formats (Excel, PDF, Word, PPT)',
                  'Advanced visualizations',
                  'Priority support',
                  'Share unlimited dashboards'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-4">
              {/* Razorpay Payment */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
                <h4 className="text-lg font-semibold mb-4 flex items-center justify-center gap-2">
                  <span className="text-2xl">💳</span> Pay with Card/UPI/Wallet
                </h4>
                <div id="razorpay-button-container" className="flex justify-center mb-3"></div>
                <p className="text-xs text-muted-foreground">Secure payment via Razorpay • Instant activation</p>
              </div>

              {/* GPay QR Code */}
              {country === 'IN' && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h4 className="text-lg font-semibold mb-4 text-center flex items-center justify-center gap-2">
                    <QrCode className="h-5 w-5" /> Pay via GPay/PhonePe
                  </h4>
                  <div className="flex flex-col items-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_ai-dash-builder-2/artifacts/7vafgrxa_WhatsApp%20Image%202026-03-17%20at%2012.30.46%20AM.jpeg" 
                      alt="GPay QR Code" 
                      className="w-48 h-48 rounded-lg border-2 border-primary/30 mb-4"
                    />
                    <p className="text-sm text-center mb-4">
                      Scan & pay ₹799, then upload screenshot below
                    </p>
                    
                    <div className="w-full space-y-3">
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={paymentEmail}
                        onChange={(e) => setPaymentEmail(e.target.value)}
                        className="w-full bg-zinc-900/50 border border-white/10 rounded-lg px-4 py-3 text-foreground"
                        required
                      />
                      
                      <input
                        type="file"
                        id="payment-proof-guest"
                        accept="image/*"
                        onChange={handleUploadPaymentProof}
                        className="hidden"
                        disabled={uploadingProof}
                      />
                      <label htmlFor="payment-proof-guest" className="block">
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full rounded-full"
                          disabled={uploadingProof || !paymentEmail}
                          onClick={() => document.getElementById('payment-proof-guest').click()}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingProof ? 'Uploading...' : 'Upload Payment Screenshot'}
                        </Button>
                      </label>
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-3 text-center">
                      Premium activated within 24 hours • Email notification sent
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                After payment, create your account to start using premium features
              </p>
            </div>
          </motion.div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-12 px-8 border-t border-white/5">
        <div className="container mx-auto max-w-6xl text-center text-muted-foreground text-sm">
          <p>© 2026 DashAI. Powered by GPT-5.2. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;